import { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectFeatureFlags, selectIsWhatsAppMessageChannelGAFlagOn } from 'core/userContext';
import { ActionMenu } from 'ui/actionMenu';
import { AllowedActionMenuElements, MENU_ALIGNMENTS, MENU_DIRECTIONS } from 'ui/actionMenu/types';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS, IconButton } from 'ui/button';
import { Checkbox } from 'ui/checkboxV2';
import { ChevronDown } from 'ui/icons';
import { EVENT_TYPES_LOOKUP, WHATS_APP_TRIGGER_EVENTS_GA } from '../constants';
import { Body2Bold, ComboButton, FlexEnd, Grid, Left, Right } from './TriggerEventGroups.styled';
import {
  getEventsForDropdown,
  areFeatureFlagsEnabled,
  areSomeFeatureFlagsEnabled,
} from './TriggerEventGroups.utils';

type Props = {
  defaultChecked: Record<string, boolean>;
  onCheckboxChange: (checked: Record<string, boolean>) => void;
};

export const TriggerEventGroups = ({ defaultChecked = {}, onCheckboxChange }: Props) => {
  const featureFlags = useSelector(selectFeatureFlags);
  const isWhatsAppGAEnabled = useSelector(selectIsWhatsAppMessageChannelGAFlagOn);

  const [checked, setChecked] = useState<Record<string, boolean>>(defaultChecked);

  const toggleEvent = (eventName: string) => ({ ...checked, [eventName]: !checked[eventName] });

  const getEventOptions = () => {
    const updatedOptions = { ...EVENT_TYPES_LOOKUP };
    if (isWhatsAppGAEnabled) updatedOptions.whatsApp.events = WHATS_APP_TRIGGER_EVENTS_GA;
    return updatedOptions;
  };

  const eventOptions = getEventOptions();

  const onSelect = (key: string) => {
    const eventsToChange = getEventsForDropdown(eventOptions, key);
    /** Determine if events should be toggled to checked or unchecked */
    const shouldEventBeChecked = key !== 'none';
    /** Generate object mapping events to their new checked values */
    const eventsToChangeMapping = eventsToChange.reduce(
      (currChecked, event) => ({
        ...currChecked,
        [event]: shouldEventBeChecked,
      }),
      { ...checked },
    );
    if (onCheckboxChange) {
      onCheckboxChange(eventsToChangeMapping);
    }
    setChecked(eventsToChangeMapping);
  };

  const handleCheckboxChange = (eventName: string) => {
    const next = toggleEvent(eventName);
    setChecked(next);
    if (onCheckboxChange) {
      onCheckboxChange(next);
    }
  };

  const isEventChecked = (eventName: string) => (eventName in checked ? checked[eventName] : false);

  useEffect(() => {
    setChecked(defaultChecked);
  }, [JSON.stringify(defaultChecked)]);

  return (
    <>
      <FlexEnd>
        <ComboButton data-test="select-combo-button">
          <Button
            data-test="combo-button-button"
            size={BUTTON_SIZES.SMALL}
            variant={BUTTON_VARIANTS.SECONDARY}
            onClick={() => {
              onSelect('all');
            }}
          >
            Select all
          </Button>
          <ActionMenu
            size={BUTTON_SIZES.SMALL}
            align={MENU_ALIGNMENTS.RIGHT}
            direction={MENU_DIRECTIONS.BOTTOM}
            options={[
              {
                id: 'none',
                content: 'None',
                as: AllowedActionMenuElements.Button,
                onClick: () => {
                  onSelect('none');
                },
              },
              ...Object.entries(eventOptions).map(([key, config]) => ({
                id: key,
                content: config.text,
                as: AllowedActionMenuElements.Button,
                onClick: () => {
                  onSelect(key);
                },
              })),
            ]}
          >
            <IconButton
              data-test={'combo-button-dropdown'}
              size={BUTTON_SIZES.SMALL}
              variant={BUTTON_VARIANTS.SECONDARY}
            >
              <ChevronDown height={16} width={16} />
            </IconButton>
          </ActionMenu>
        </ComboButton>
      </FlexEnd>
      <Grid>
        {Object.keys(eventOptions).map((eventTypeGroup) => {
          const eventOption = eventOptions[eventTypeGroup as keyof typeof eventOptions];

          const {
            events: eventLabelLookup,
            requiredFeatureFlags = [],
            requiredFeatureFlagsByEvent,
            requireOnlyOneFlag = false,
          } = eventOption;

          const groupIsVisible = requiredFeatureFlagsByEvent
            ? areSomeFeatureFlagsEnabled(
                Object.keys(eventLabelLookup),
                requiredFeatureFlagsByEvent,
                featureFlags,
              )
            : areFeatureFlagsEnabled(requiredFeatureFlags, featureFlags, requireOnlyOneFlag);

          if (!groupIsVisible) return null;

          return (
            <Fragment key={eventTypeGroup}>
              <Left data-test={`event-type-${eventTypeGroup}`}>
                <Body2Bold>{eventOption.text}</Body2Bold>
              </Left>
              <Right data-test={`event-type-${eventTypeGroup}-options`}>
                {Object.keys(eventLabelLookup).map((eventName) => {
                  const eventChecked = isEventChecked(eventName);

                  if (requiredFeatureFlagsByEvent) {
                    const requiredFlags = requiredFeatureFlagsByEvent[eventName] || [];
                    // If any of the required feature flags are not enabled, don't show
                    // the event
                    const eventIsVisible = areFeatureFlagsEnabled(requiredFlags, featureFlags);
                    if (!eventIsVisible) return null;
                  }

                  return (
                    <Checkbox
                      key={eventName}
                      id={eventName}
                      name={eventName}
                      label={eventLabelLookup[eventName as keyof typeof eventLabelLookup]}
                      status={eventChecked}
                      setChecked={() => {
                        handleCheckboxChange(eventName);
                      }}
                      data-test={`${eventName}Checkbox-${eventChecked ? 'checked' : 'unchecked'}`}
                    />
                  );
                })}
              </Right>
            </Fragment>
          );
        })}
      </Grid>
    </>
  );
};
